import { usePathname, useRouter, useSegments } from "expo-router";
import { useEffect } from "react";
import { Platform } from "react-native";

import { isMobileUserAgent } from "@/lib/mobile-web";
import { needsProfileSetup } from "@/lib/onboarding";
import {
  clearDestination,
  peekDestination,
  rememberDestination,
} from "@/lib/pending-destination";
import { useAuthStore } from "@/state/auth-store";

import {
  nextRedirectDelay,
  redirectTarget,
  REDIRECT_RETRY_MS,
} from "./auth-routing-core";

// Constant for the life of the page, so computed once rather than per render.
const MOBILE_WEB =
  Platform.OS === "web" &&
  typeof navigator !== "undefined" &&
  isMobileUserAgent(navigator.userAgent ?? "");

/**
 * How long a redirect may go unlanded before it is worth saying so. NOT a stop:
 * the retry below is never abandoned, because the condition it waits on cannot
 * clear itself.
 *
 * The previous shape gave up after 25 x 120ms and left the app wherever it was
 * for good. Its effect keys on `target`, which does not change while the
 * redirect is being dropped, so nothing re-armed it and no later render tried
 * again. An iOS Google sign-in that had to show a password page outran that 3s
 * budget every time and stranded an AUTHENTICATED user on the welcome screen,
 * with one console warning as the only trace.
 *
 * A longer fixed budget would only move the cliff further out; the sign-in was
 * racing a native modal, and no constant is the right guess for that on every
 * device. Backoff removes the cliff instead.
 */
const REDIRECT_WARN_AFTER_MS = 3000;

/**
 * Central navigation guard. Keeps the user in the correct route group based on
 * auth + onboarding state, and routes onboarding to the first incomplete step.
 */
export function useAuthRouting() {
  const router = useRouter();
  // Widened from expo-router's per-route tuple union, which types the depth of
  // each known route and so rejects reading index 1 on a one-segment route.
  const segments: readonly string[] = useSegments();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const onboarding = useAuthStore((s) => s.onboarding);
  const pathname = usePathname();

  // The bounce is where the invitation would be lost, so the bounce is where
  // it is remembered: an unauthenticated visitor about to be sent to welcome
  // from a resumable deep link (invite envelope, group join) records where
  // they were going. `rememberDestination` refuses everything else.
  //
  // And ARRIVAL is the consumption: the slot is cleared only once an
  // authenticated user is actually standing on the remembered screen, never
  // when the redirect is merely issued — expo-router can drop an early
  // replace (see the effect below), and a slot cleared for a redirect that
  // never landed is the invitation lost a second way. The authenticated
  // check keeps a signed-out visitor VIEWING the invite from wiping the very
  // slot being remembered.
  if (status === "unauthenticated") {
    rememberDestination(pathname);
  } else if (status === "authenticated" && pathname === peekDestination()) {
    clearDestination();
  }

  // Where this user belongs, or null when they are already somewhere valid.
  // Derived at render rather than inside the effect so the effect depends on a
  // plain string: `segments` is a fresh array every render, and keying the
  // redirect to it re-ran this on renders that changed nothing.
  const target = redirectTarget({
    group: segments[0],
    step: segments[1],
    status,
    needsProfile: needsProfileSetup(user),
    permissionsDone: onboarding.permissionsDone,
    mobileWeb: MOBILE_WEB,
    pendingHref: peekDestination(),
  });

  useEffect(() => {
    if (!target) return undefined;

    let attempts = 0;

    // Re-issued rather than fired once, because expo-router can drop it.
    // `router.replace` appends to a queue that is drained as soon as anything
    // is queued; the drain empties the queue first and dispatches only if the
    // NavigationContainer ref has attached, so a redirect issued a frame too
    // early is discarded silently — expo-router's own source flags this as a
    // TODO. Nothing logs, and every input to this hook stays exactly as it
    // was, so no later render retries: the app sits on the entry splash
    // forever. A warm start is where it bites, since the stored session
    // resolves fast enough to beat the container mount.
    //
    // Landing changes `segments`, which clears `target` and stops the timer.
    router.replace(target);

    const startedAt = Date.now();
    let delay = REDIRECT_RETRY_MS;
    let warned = false;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const retry = () => {
      if (cancelled) return;
      attempts += 1;

      // Said once, and the retry continues. Silence was how the old give-up
      // hid; a warning that also stops is how it looked deliberate.
      if (!warned && Date.now() - startedAt >= REDIRECT_WARN_AFTER_MS) {
        warned = true;
        console.warn(
          `[routing] redirect to ${String(target)} has not taken effect after ` +
            `${attempts} attempts; still retrying`,
        );
      }

      router.replace(target);
      delay = nextRedirectDelay(delay);
      timer = setTimeout(retry, delay);
    };

    timer = setTimeout(retry, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `pathname` is a dependency so that ARRIVING somewhere still wrong (a
    // multi-step onboarding hop, a partial navigation) restarts the sequence at
    // full speed rather than continuing at the backed-off cadence. Landing on
    // the target clears `target` itself, which tears the timer down.
  }, [target, pathname, router]);
}
